import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BookOpen,
  Plus,
  X,
  Save,
  Sparkles,
  Search,
  Calendar,
  Building,
  User,
  Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Templates = () => {
  // Estados básicos
  const [loading, setLoading] = useState(false);
  const [cargoDescricao, setCargoDescricao] = useState('');
  const [generating, setGenerating] = useState(false);
  
  // Estados para sistema de abas e templates criados
  const [activeTab, setActiveTab] = useState('assistente');
  const [templatesCriados, setTemplatesCriados] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [filtroNome, setFiltroNome] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [templateSelecionado, setTemplateSelecionado] = useState(null);
  const [salvandoTemplate, setSalvandoTemplate] = useState(false);
  const [sugerindoCbo, setSugerindoCbo] = useState(false);

  // Carregar templates criados
  const loadTemplatesCriados = async () => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const params = filtroNome ? `?nome_filter=${filtroNome}` : '';
      const response = await axios.get(
        `${API}/api/dcf/templates/lista-completa${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.success) {
        setTemplatesCriados(response.data.templates || []);
      }
    } catch (error) {
      console.error('Erro ao carregar templates criados:', error);
      setTemplatesCriados([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Abrir template para edição
  const abrirTemplateParaEdicao = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/dcf/templates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.success) {
        setTemplateSelecionado(response.data.template);
        setShowEditModal(true);
      }
    } catch (error) {
      console.error('Erro ao buscar template:', error);
      alert('❌ Erro ao carregar template para edição');
    }
  };

  // Salvar template
  const salvarTemplate = async () => {
    if (!templateSelecionado) return;
    
    setSalvandoTemplate(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/api/dcf/templates/${templateSelecionado.id}`,
        templateSelecionado,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.success) {
        alert('✅ Template atualizado com sucesso!');
        setShowEditModal(false);
        await loadTemplatesCriados();
      }
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      alert('❌ Erro ao salvar template. Tente novamente.');
    } finally {
      setSalvandoTemplate(false);
    }
  };

  // Criar cargo de template
  const criarCargoDeTemplate = async () => {
    if (!templateSelecionado) return;
    
    if (!window.confirm('Deseja criar um cargo DCF a partir deste template?')) {
      return;
    }
    
    setSalvandoTemplate(true);
    try {
      const token = localStorage.getItem('token');
      
      // Primeiro salvar as alterações do template
      await axios.put(
        `${API}/api/dcf/templates/${templateSelecionado.id}`,
        templateSelecionado,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Depois criar o cargo
      const response = await axios.post(
        `${API}/api/dcf/templates/${templateSelecionado.id}/criar-cargo`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.success) {
        alert(`✅ ${response.data.message}\n\nO cargo foi adicionado em "Gerenciar Cargo"!`);
        setShowEditModal(false);
        await loadTemplatesCriados();
      }
    } catch (error) {
      console.error('Erro ao criar cargo:', error);
      alert('❌ Erro ao criar cargo. Tente novamente.');
    } finally {
      setSalvandoTemplate(false);
    }
  };

  // Gerar template com IA
  const handleGerarTemplateIA = async () => {
    if (!cargoDescricao.trim()) {
      alert('❌ Por favor, descreva o cargo que você quer criar');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/dcf/templates/gerar-com-ia`,
        { cargo_descricao: cargoDescricao },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data) {
        alert('✅ Template gerado com sucesso! Veja na aba "Templates Criados".');
        setCargoDescricao('');
        setActiveTab('templates-criados');
        await loadTemplatesCriados();
      }
    } catch (error) {
      console.error('Erro ao gerar template com IA:', error);
      alert('❌ Erro ao gerar template. Tente novamente ou crie manualmente.');
    } finally {
      setGenerating(false);
    }
  };

  // Atualizar campo do template
  const atualizarCampoTemplate = (campo, valor) => {
    setTemplateSelecionado({
      ...templateSelecionado,
      [campo]: valor
    });
  };

  // Atualizar atividade
  const atualizarAtividade = (index, campo, valor) => {
    const novasAtividades = [...(templateSelecionado.atividades || [])];
    novasAtividades[index] = {
      ...novasAtividades[index],
      [campo]: valor
    };
    atualizarCampoTemplate('atividades', novasAtividades);
  };


  // Sugerir CBO com IA
  const sugerirCBO = async () => {
    if (!templateSelecionado?.nome_cargo || !templateSelecionado.nome_cargo.trim()) {
      alert('❌ Por favor, preencha o nome do cargo primeiro');
      return;
    }
    
    setSugerindoCbo(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/dcf/sugerir-cbo`,
        { cargo_nome: templateSelecionado.nome_cargo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.success && response.data.cbo) {
        const cboData = response.data.cbo;
        atualizarCampoTemplate('cbo', cboData.codigo_cbo);
        
        // Mostrar informação sobre o CBO sugerido
        alert(`✅ CBO Sugerido: ${cboData.codigo_cbo}\n\n${cboData.titulo_cbo}\n\n${cboData.justificativa}`);
      }
    } catch (error) {
      console.error('Erro ao sugerir CBO:', error);
      alert('❌ Erro ao sugerir CBO. Você pode digitar manualmente.');
    } finally {
      setSugerindoCbo(false);
    }
  };


  // useEffect para carregar templates quando mudar de aba
  useEffect(() => {
    if (activeTab === 'templates-criados') {
      loadTemplatesCriados();
    }
  }, [activeTab, filtroNome]);

  const totalTemplates = templatesCriados.length;
  const departamentosCount = new Set(templatesCriados.map((template) => template.departamento).filter(Boolean)).size;
  const niveisCount = new Set(templatesCriados.map((template) => template.nivel_hierarquico).filter(Boolean)).size;
  const totalAtividades = templatesCriados.reduce((sum, template) => sum + (template.total_atividades || 0), 0);

  return (
    <div className="p-6 space-y-6">
      <section className="overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-violet-900 text-white shadow-xl">
        <div className="grid gap-8 p-6 lg:grid-cols-[1.5fr_1fr] lg:p-8">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-violet-100">
              <BookOpen className="h-3.5 w-3.5" />
              Templates DCF
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight lg:text-4xl">Estruture cargos novos sem começar do zero.</h1>
              <p className="mt-3 max-w-2xl text-sm text-slate-200 lg:text-base">
                Use IA para gerar uma base inicial, revisar os campos críticos e transformar templates em cargos efetivos sem perder controle do desenho organizacional.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <button
                type="button"
                onClick={() => setActiveTab('assistente')}
                className={`rounded-2xl px-4 py-4 text-left transition-transform hover:-translate-y-0.5 ${
                  activeTab === 'assistente' ? 'bg-white text-slate-900' : 'border border-white/15 bg-white/10 text-white hover:bg-white/15'
                }`}
              >
                <Sparkles className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Assistente IA</p>
                <p className={`mt-1 text-sm ${activeTab === 'assistente' ? 'text-slate-600' : 'text-slate-200'}`}>Gerar uma primeira versão de template.</p>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('templates-criados')}
                className={`rounded-2xl px-4 py-4 text-left transition-transform hover:-translate-y-0.5 ${
                  activeTab === 'templates-criados' ? 'bg-white text-slate-900' : 'border border-white/15 bg-white/10 text-white hover:bg-white/15'
                }`}
              >
                <BookOpen className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Templates criados</p>
                <p className={`mt-1 text-sm ${activeTab === 'templates-criados' ? 'text-slate-600' : 'text-slate-200'}`}>Editar, revisar e publicar modelos salvos.</p>
              </button>
              <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4">
                <Building className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Departamentos</p>
                <p className="mt-1 text-sm text-slate-200">Templates espalhados por {departamentosCount} área(s).</p>
              </div>
              <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4">
                <Calendar className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Atividades modeladas</p>
                <p className="mt-1 text-sm text-slate-200">{totalAtividades} atividades já consolidadas na base.</p>
              </div>
            </div>
          </div>

          <div className="rounded-[24px] border border-white/10 bg-white/10 p-5 backdrop-blur">
            <p className="text-sm font-medium text-slate-200">Panorama da biblioteca</p>
            <div className="mt-5 space-y-3 text-sm text-slate-100">
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Templates salvos</span>
                <strong>{totalTemplates}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Níveis hierárquicos</span>
                <strong>{niveisCount}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Filtro ativo</span>
                <strong>{filtroNome ? 'Sim' : 'Não'}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-violet-300/20 px-4 py-3 text-violet-100">
                <span>Aba atual</span>
                <strong>{activeTab === 'assistente' ? 'IA' : 'Biblioteca'}</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Templates criados</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{totalTemplates}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Departamentos</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{departamentosCount}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Níveis mapeados</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{niveisCount}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Atividades nos modelos</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{totalAtividades}</p>
        </article>
      </section>

      {/* Conteúdo das Abas */}
      {activeTab === 'assistente' && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.3fr_0.7fr]">
          <div className="rounded-3xl border border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50 p-8 shadow-sm">
            <div className="mb-6 flex items-center space-x-3">
              <div className="rounded-full bg-purple-100 p-3">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">🤖 Assistente IA para DCF</h3>
                <p className="text-sm text-gray-600">Crie templates personalizados com ajuda da inteligência artificial</p>
              </div>
            </div>
            
            <div className="mb-6 rounded-2xl bg-white p-6 shadow-sm">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descreva o cargo que você quer criar:
              </label>
              <input 
                type="text" 
                value={cargoDescricao}
                onChange={(e) => setCargoDescricao(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleGerarTemplateIA()}
                placeholder="Ex: Analista de Marketing Digital"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={generating}
              />
            </div>
            
            <div className="mb-6 text-sm text-gray-600">
              <p className="font-medium mb-2">🎯 Sugestões automáticas baseadas em:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Mercado atual</li>
                <li>Cargos similares no sistema</li>
                <li>Melhores práticas de RH</li>
                <li>Base de dados de competências</li>
              </ul>
            </div>
            
            <button 
              onClick={handleGerarTemplateIA}
              disabled={generating}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {generating ? (
                <>
                  <Clock className="w-5 h-5 animate-spin" />
                  <span>Gerando Template...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  <span>Gerar Template com IA</span>
                </>
              )}
            </button>
          </div>

          <div className="space-y-5">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Como usar</p>
              <h3 className="mt-2 text-xl font-semibold text-slate-900">Fluxo recomendado</h3>
              <div className="mt-5 space-y-4 text-sm text-slate-600">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="font-medium text-slate-800">1. Escreva o cargo com clareza</p>
                  <p className="mt-1">Informe o nome e, se possível, o contexto da área e senioridade.</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="font-medium text-slate-800">2. Revise o modelo gerado</p>
                  <p className="mt-1">Ajuste CBO, atividades, competências e requisitos antes de publicar.</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="font-medium text-slate-800">3. Converta em cargo real</p>
                  <p className="mt-1">Quando o template estiver consistente, promova-o para a estrutura oficial do DCF.</p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Observação</p>
              <p className="mt-2 text-sm text-slate-600">
                A IA acelera o ponto de partida, mas não substitui a revisão do RH ou da liderança da área.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'templates-criados' && (
        <div className="space-y-6">
          {/* Filtro por nome */}
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Biblioteca</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">Templates salvos</h2>
                <p className="mt-2 text-sm text-slate-600">Busque rapidamente pelo nome do cargo e abra o template para revisão detalhada.</p>
              </div>
              <button
                type="button"
                onClick={() => setFiltroNome('')}
                className="inline-flex items-center justify-center rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Limpar busca
              </button>
            </div>

            <div className="relative mt-6 max-w-md">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar template por nome do cargo..."
                value={filtroNome}
                onChange={(e) => setFiltroNome(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          {/* Loading */}
          {loadingTemplates && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Carregando templates...</p>
            </div>
          )}

          {/* Templates em Cards */}
          {!loadingTemplates && templatesCriados.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Nenhum template encontrado</p>
              <p className="text-gray-500 text-sm mt-2">Gere seu primeiro template usando o Assistente IA</p>
            </div>
          )}

          {!loadingTemplates && templatesCriados.length > 0 && (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
              {templatesCriados.map((template) => (
                <div 
                  key={template.id}
                  className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-transform hover:-translate-y-0.5 cursor-pointer"
                  onClick={() => abrirTemplateParaEdicao(template.id)}
                >
                  <div className="border-b border-slate-200 bg-slate-50 p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-gray-900 mb-1">{template.nome_cargo}</h3>
                        <span className="inline-block rounded-full border border-purple-200 bg-purple-100 px-2 py-1 text-xs text-purple-700">
                          Gerado por IA
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Building className="w-4 h-4" />
                        <span>{template.departamento || 'Sem departamento'}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-gray-600">
                        <User className="w-4 h-4" />
                        <span className="capitalize">{template.nivel_hierarquico || 'Não definido'}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(template.criado_em).toLocaleDateString('pt-BR')}</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Atividades</p>
                          <p className="mt-1 text-sm font-medium text-slate-900">{template.total_atividades || 0}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Ação</p>
                          <p className="mt-1 text-sm font-medium text-slate-900">Abrir para revisar</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal de Edição Grande */}
      {showEditModal && templateSelecionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[28px] border border-slate-200 shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header do Modal */}
            <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-violet-900 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Editar Template</h2>
                <p className="text-violet-100 text-sm mt-1">Revise o modelo antes de salvar ou converter em cargo.</p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Conteúdo do Modal - Scroll */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Informações Básicas */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Básicas</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome do Cargo *
                      </label>
                      <input
                        type="text"
                        value={templateSelecionado.nome_cargo || ''}
                        onChange={(e) => atualizarCampoTemplate('nome_cargo', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Código do Cargo
                      </label>
                      <input
                        type="text"
                        value={templateSelecionado.codigo_cargo || ''}
                        onChange={(e) => atualizarCampoTemplate('codigo_cargo', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        CBO (Classificação Brasileira de Ocupações)
                        <button
                          type="button"
                          onClick={sugerirCBO}
                          disabled={sugerindoCbo}
                          className="text-xs bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700 disabled:opacity-50"
                        >
                          {sugerindoCbo ? '⏳ Sugerindo...' : '✨ Sugerir IA'}
                        </button>
                      </label>
                      <input
                        type="text"
                        value={templateSelecionado.cbo || ''}
                        onChange={(e) => atualizarCampoTemplate('cbo', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Ex: 2124-05"
                        maxLength="7"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Departamento
                      </label>
                      <input
                        type="text"
                        value={templateSelecionado.departamento || ''}
                        onChange={(e) => atualizarCampoTemplate('departamento', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nível Hierárquico
                      </label>
                      <select
                        value={templateSelecionado.nivel_hierarquico || ''}
                        onChange={(e) => atualizarCampoTemplate('nivel_hierarquico', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Selecione...</option>
                        <option value="junior">Júnior</option>
                        <option value="pleno">Pleno</option>
                        <option value="senior">Sênior</option>
                        <option value="gerencial">Gerencial</option>
                        <option value="diretoria">Diretoria</option>
                      </select>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Superior Imediato
                      </label>
                      <input
                        type="text"
                        value={templateSelecionado.superior_imediato || ''}
                        onChange={(e) => atualizarCampoTemplate('superior_imediato', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Objetivo do Cargo */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Objetivo do Cargo</h3>
                  <textarea
                    value={templateSelecionado.objetivo_cargo || ''}
                    onChange={(e) => atualizarCampoTemplate('objetivo_cargo', e.target.value)}
                    rows="3"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Descreva o objetivo principal do cargo..."
                  />
                </div>

                {/* Atividades */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Atividades</h3>
                  <div className="space-y-4">
                    {(templateSelecionado.atividades || []).map((atividade, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="grid grid-cols-12 gap-4">
                          <div className="col-span-8">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Descrição
                            </label>
                            <input
                              type="text"
                              value={atividade.descricao || ''}
                              onChange={(e) => atualizarAtividade(index, 'descricao', e.target.value)}
                              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Tipo
                            </label>
                            <select
                              value={atividade.tipo || 'essencial'}
                              onChange={(e) => atualizarAtividade(index, 'tipo', e.target.value)}
                              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="essencial">Essencial</option>
                              <option value="complementar">Complementar</option>
                              <option value="eventual">Eventual</option>
                            </select>
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              % Tempo
                            </label>
                            <input
                              type="number"
                              value={atividade.percentual_tempo || 0}
                              onChange={(e) => atualizarAtividade(index, 'percentual_tempo', parseInt(e.target.value) || 0)}
                              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                              min="0"
                              max="100"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Competências */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Competências Essenciais</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Competências Técnicas
                      </label>
                      <textarea
                        value={(templateSelecionado.competencias_essenciais?.tecnicas || []).join('\n')}
                        onChange={(e) => atualizarCampoTemplate('competencias_essenciais', {
                          ...templateSelecionado.competencias_essenciais,
                          tecnicas: e.target.value.split('\n').filter(c => c.trim())
                        })}
                        rows="3"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Uma competência por linha..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Competências Comportamentais
                      </label>
                      <textarea
                        value={(templateSelecionado.competencias_essenciais?.comportamentais || []).join('\n')}
                        onChange={(e) => atualizarCampoTemplate('competencias_essenciais', {
                          ...templateSelecionado.competencias_essenciais,
                          comportamentais: e.target.value.split('\n').filter(c => c.trim())
                        })}
                        rows="3"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Uma competência por linha..."
                      />
                    </div>
                  </div>
                </div>

                {/* Formação e Experiência */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Formação e Experiência</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nível Mínimo de Formação
                      </label>
                      <select
                        value={templateSelecionado.formacao_academica?.nivel_minimo || ''}
                        onChange={(e) => atualizarCampoTemplate('formacao_academica', {
                          ...templateSelecionado.formacao_academica,
                          nivel_minimo: e.target.value
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Selecione...</option>
                        <option value="ensino_medio">Ensino Médio</option>
                        <option value="tecnico">Técnico</option>
                        <option value="superior">Superior</option>
                        <option value="pos_graduacao">Pós-Graduação</option>
                        <option value="mestrado">Mestrado</option>
                        <option value="doutorado">Doutorado</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tempo Mínimo de Experiência (anos)
                      </label>
                      <input
                        type="number"
                        value={templateSelecionado.experiencia?.tempo_minimo || 0}
                        onChange={(e) => atualizarCampoTemplate('experiencia', {
                          ...templateSelecionado.experiencia,
                          tempo_minimo: parseInt(e.target.value) || 0
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer do Modal com Botões */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={salvarTemplate}
                disabled={salvandoTemplate}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{salvandoTemplate ? 'Salvando...' : 'Salvar Template'}</span>
              </button>
              <button
                onClick={criarCargoDeTemplate}
                disabled={salvandoTemplate}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>{salvandoTemplate ? 'Criando...' : 'Criar Cargo'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;
